<template>
  <div class="tag-select">
    <v-select
      multiple
      :taggable="canCreate"
      :filterable="false"
      :closeOnSelect="false"
      :options="results"
      :createOption="createOption"
      :value="labeledValue"
      @search="doSearch"
      @input="onInput"
      @keyup.native="handleEmptySearch"
      :placeholder="placeholder">
      <template slot="no-options">
        Search tags...
      </template>
    </v-select>
  </div>
</template>

<script>

  import debounce from 'debounce';

  export default {
    name: 'tag-select',

    props: {
      placeholder: {
        type: String,
        default: 'Example'
      },
      search: {
        type: Function,
        /**
         * @async
         * @param {string} searchTerm
         * @returns {object[]} tags
         * @returns {number}   tags[].id
         * @returns {string}   tags[].name
         */
      },
      canCreate: {
        type: Boolean,
        default: false
      },

      value: {
        type: Array,
        // [ { id, name } ]
        // v-model is supported
      }
    },

    data() {
      return {
        results: [],
      };
    },

    methods: {
      reset() {
        this.results = [];
        this.$emit('input', []);
      },

      createOption(newOption) {
        // v-select will call this before creating new tags, to give us the
        // option to customize them. We want to make sure to give the options
        // both a label (for v-select) and a name (for us).
        if (typeof newOption === 'string') {
          newOption = {
            label: newOption.trim()
          };
        }
        newOption.label = newOption.label.trim();
        newOption.name = newOption.label;
        return newOption;
      },

      onInput(newSelected) {
        // vue-select likes to say the input has changed when it really hasn't,
        // which causes headaches for us elsewhere. Do a deep compare to make
        // sure something has actually changed.
        if (JSON.stringify(this.value) === JSON.stringify(newSelected)) {
          return;
        }

        // Clear the search results each time a tag is selected
        this.results = [];

        // Save the new tags
        this.$emit('input', newSelected);
      },

      doSearch: debounce(async function(searchTerm, setLoading) {
        const self = this;
        setLoading(true);
        const rawNewResults = await self.search(searchTerm.trim());
        self.results = rawNewResults.map(tag => {
          return {
            id:    tag.id,
            name:  tag.name,
            label: tag.name
          };
        });
        setLoading(false);
      }, 500),      

      handleEmptySearch(ev) {
        // search() is only called when the search term is non-empty (this is a
        // known bug in the library); this method should check for an empty
        // search string and clear the results when found.
        if (ev.target.value.length === 0) {
          this.results = [];
        }
      },
    },

    computed: {

      labeledValue() {
        return this.value.map(t => {
          return {
            id: t.id,
            name: t.name,
            label: t.label || t.name,
          };
        });
      },
    },
  }
</script>

<style lang="scss" scoped>
  .clear-button {
    border: none;
    background: none;
  }

  .v-select {
    border-bottom: 1px solid #fff;
    font-family: inherit;
  }
</style>

<style lang="scss">
  .tag-select {
    .v-select {
      padding: 4px 0;

      input {
        border-bottom: none !important;
      }
      .selected-tag {
        background: transparent;
        border: solid 1px #33ccff;
        border-radius: 4px;
        color: #33ccff;
        margin: 4px 5px 4px 0;
        padding: 3px 8px;
        line-height: 18px;
        font-size: 12px;
        &::before {
          content: '#';
        }
        .close {
          color: #33ccff;
          opacity: 0.8;
          line-height: 17px;
          font-size: 16px;
        }
      }

      .dropdown-toggle {
        border: none;
      }

      .dropdown-menu {
        background-color: #363636;
        border: 1px solid #666;
        padding: 0;
        overflow-y: auto;
        max-height: 200px;
        li a {
          color: #ebebeb;
        }
        li.highlight a {
          color: white;
        }
      }

      .clear {
        color: #555;
      }

      .open-indicator::before {
        border-color: #b0b0b0;
      }

      .spinner {
        border-top-color: rgba(255, 255, 255, 0.2);
        border-right-color: rgba(255, 255, 255, 0.2);
        border-bottom-color: rgba(255, 255, 255, 0.2);
        border-left-color: rgba(51, 204, 255, 0.65);
      }
    }
  }
</style>



// WEBPACK FOOTER //
// index.vue?41ba1aa1